import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import styles from "./App.module.css";
import { APP_STORE_URL, MarketingFooter, MarketingNav } from "./Components/MarketingSite";
import { trackEvent } from "./analytics";

const quickLinks = [
  ["Getting started", "#getting-started", "Create images, use credits, and find your history."],
  ["Credits", "#credits", "How free credits, plans, and earlier purchases work."],
  ["Creative tools", "#creative-tools", "Image, chat, audio, and roadmap status."],
  ["Subscriptions and account", "#subscriptions", "Cancel, restore, refunds, and deleting your account."],
  ["Privacy and processing", "#privacy-support", "What runs locally, what may use cloud help."],
];

const productStatus = [
  ["iPhone app", "Available now", "The main Creata AI experience is built for iPhone."],
  ["Daily free credits", "Available now", "Start creating without a subscription."],
  ["Starter and Pro plans", "Available now", "More credits each month, plus MP3, podcast, song and Voice Changer."],
  ["Chat-first home", "Available now", "Type, paste a link, or share a page, then listen, summarize, or ask questions about it."],
  ["Image creation + chat", "Available now", "Create from prompts, photos, templates, and chat-led iteration."],
  ["Magic Edit", "Available now", "Edit a photo by describing the change. Rotate and flip are free."],
  ["Music generation", "Available now", "Turn a text direction into a short musical idea."],
  ["Text to speech", "Available now", "Give scripts, notes, and ideas a voice."],
  ["Voice clone and Voice Changer", "Available now", "Use voice samples you own or have permission to use, or re-record a clip in another voice (Starter and Pro)."],
  ["MP3, podcast and song", "Available now", "Turn text into an MP3, a two-host podcast, or a song with vocals (Starter and Pro)."],
  ["Video", "Roadmap", "Coming later; the app will label video features clearly when ready."],
];

const faqs = [
  {
    id: "getting-started",
    title: "How do I start creating?",
    items: [
      "Download Creata AI from the App Store, open the Create screen, then start with a prompt, photo, or quick template.",
      "Use the creative tools and chat to refine the direction, then save or share the result from the app.",
      "Your recent work and next tools stay close so you can keep iterating without starting over.",
    ],
  },
  {
    id: "credits",
    title: "How do credits work?",
    items: [
      "Creata AI includes free credits every month, plus a daily claim in the app. Starter ($4.99 a month) and Pro ($9.99 a month) give more credits and unlock MP3, podcast, song and Voice Changer. Prices are US dollars through the App Store and may differ in your country.",
      "Your credits come from the free monthly allowance, the credits you can claim once a day in the app, and your plan. Free credits are used first.",
      "When you run out, tap your credit balance at the top of the home screen and choose Get more with Starter or Pro, or open Profile and tap Subscribe. The app also shows the price of each action before you confirm it.",
      "Credits you bought earlier on creataai.com stay on your account, never expire, and are used after your free credits. If something looks wrong with them, email support.",
    ],
  },
  {
    id: "creative-tools",
    title: "What can Creata AI make now?",
    items: [
      "Creata AI supports prompt-to-image creation, photo-led visual exploration, templates, and chat-assisted iteration.",
      "Chat-first home: listen to articles, get summaries, ask questions about a page, and make an MP3, a two-host podcast, or a song with vocals from your text (MP3, podcast and song are on Starter and Pro).",
      "Audio tools also include music generation, text to speech, voice clone, and Voice Changer. Sound effects are still in development.",
      "Video is on the roadmap. Until it ships, video-related experiences will be described as coming soon.",
    ],
  },
  {
    id: "sharing",
    title: "How do I send a page or link to Creata?",
    items: [
      "Paste the link into the chat box, or use Share > Creata in Safari or another app.",
      "In Safari you can also turn on the Creata extension (page menu > Manage Extensions) and tap the floating button to send the page you are reading, even behind a login.",
      "For an Apple News story, share it to Creata or take screenshots of it and choose to read it from screenshots.",
    ],
  },
  {
    id: "library-reminders",
    title: "Where do my results go, and what are reminders?",
    items: [
      "Text and audio you make are saved in your Library. Generated audio is kept for 7 days, so save or share anything you want to keep longer.",
      "Tap Remind me on a result to get a notification on your iPhone. Reminders are stored on your device only.",
    ],
  },
  {
    id: "subscriptions",
    title: "How do I cancel, restore, or get a refund for a subscription?",
    items: [
      "Starter and Pro are billed by Apple. To cancel, open the iPhone Settings app, tap your name, then Subscriptions, and choose Creata AI. You keep access until the end of the period you paid for.",
      "To restore a purchase on a new phone, open the subscription screen in the app (Profile, then Subscribe) and tap Restore Purchase. Use the same Apple Account you subscribed with.",
      "Refunds are decided by Apple. Request one at reportaproblem.apple.com. If the app does not show your plan after a purchase or restore, email us with your Creata AI username.",
    ],
    action: ["Request a refund from Apple", "https://reportaproblem.apple.com"],
  },
  {
    id: "delete-account",
    title: "How do I delete my account?",
    items: [
      "Open the Profile tab and tap the delete-account button in the row of round buttons at the bottom. You confirm twice before anything happens.",
      "Your account is marked for deletion and the app starts fresh on this phone, with the saved data on the device cleared. A subscription is not cancelled by deleting the account, so cancel it in iPhone Settings first.",
      "To ask us to remove the data we hold about you, including the results you made, email support with your Creata AI username.",
    ],
    action: ["Email support", "mailto:creataAi.com@gmail.com"],
  },
  {
    id: "ai-permissions",
    title: "How do I stop photos, voice or text being sent to AI services?",
    items: [
      "The app asks before the first photo, recording or text is sent, and names who receives it. If you tap Don't Allow, nothing is sent.",
      "To take back permission you already gave, open the Profile tab, go to About, and tap Reset next to AI data-sharing. The app will ask again next time.",
      "Listening with your phone's own voice, saving a voice file, reading text from screenshots and reminders do not send anything.",
    ],
    action: ["Read the privacy notice", "/privacy.html"],
  },
  {
    id: "turn-on-sharing",
    title: "How do I turn on Share to Creata and the Safari button?",
    items: [
      "Share to Creata: in any app, tap Share, then More if Creata is not in the row, and choose Creata.",
      "Safari button: in Safari, tap the page menu (the icon at the left of the address bar), choose Manage Extensions, and turn on Creata. Then allow it for the website you are on.",
      "A round C button appears on the page. Tap it, choose Send this page, and Creata opens with the choices for that page.",
    ],
    action: ["Open the full Safari guide", "/safari"],
  },
  {
    id: "notifications",
    title: "Why am I not getting reminders?",
    items: [
      "Reminders use iPhone notifications. The first time you set one, iOS asks for permission. If you said no, open the iPhone Settings app, tap Notifications, choose Creata AI, and allow notifications.",
      "The Reminders screen in the app tells you when notifications are off and links to your settings.",
    ],
  },
  {
    id: "report-content",
    title: "How do I report or hide something I see in Explore?",
    items: [
      "In a Discover image, tap the report or hide button. Choose Report and pick a reason, or choose Hide this creator's content to stop seeing that person's images on this device.",
      "For anything urgent or abusive, email support with a description of what you saw.",
    ],
    action: ["Email support", "mailto:creataAi.com@gmail.com"],
  },
  {
    id: "macos",
    title: "Where is Mac support?",
    items: [
      "The current primary app experience is iPhone-first.",
      "Questions specific to the older macOS app are kept on the Mac support page so device-specific instructions do not get mixed together.",
    ],
    action: ["Open Mac support", "/macosapp"],
  },
  {
    id: "privacy-support",
    title: "Does Creata AI process everything on my device?",
    items: [
      "No. Some things run on your device: listening with your phone's voice, saving a voice file, reading text from screenshots, reminders, and the Safari button's page capture.",
      "Other things use AI services in the cloud, such as chat, summaries, images, Magic Edit, and MP3, podcast and song. Before a photo, recording, or text is sent, the app asks and names who receives it. You can withdraw permission in Profile.",
      "The privacy notice lists each provider and what it receives.",
    ],
  },
  {
    id: "contact",
    title: "How do I get help with an account or purchase?",
    items: [
      "Email support with your Creata AI username, the device you are using, and a short description of what happened.",
      "For credits you bought earlier on creataai.com, include the checkout email and approximate purchase time. For a subscription, include your Apple Account email. Do not send passwords or private prompts.",
    ],
    action: ["Email support", "mailto:creataAi.com@gmail.com"],
  },
];

const Support = () => {
  const trackSupportClick = (destination) => {
    trackEvent("support_link_click", { destination });
  };

  return (
    <main className={styles.landingPage}>
      <MarketingNav currentPage="support" />

      <section className={styles.supportHero} aria-labelledby="support-title">
        <div className={styles.supportHeroCopy}>
          <p className={styles.eyebrow}><HelpOutlineIcon fontSize="small" /> Support</p>
          <h1 id="support-title">Help for the current Creata AI app.</h1>
          <p>
            Find the up-to-date basics for creating on iPhone, using credits, trying audio tools,
            and understanding what is available now versus what is still on the roadmap.
          </p>
          <div className={styles.heroActions}>
            <a
              className={styles.primaryCta}
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackSupportClick("app_store")}
            >
              Download for iPhone <ArrowForwardIcon fontSize="small" />
            </a>
            <a
              className={styles.textCta}
              href="mailto:creataAi.com@gmail.com"
              onClick={() => trackSupportClick("email")}
            >
              Contact support
            </a>
          </div>
        </div>
        <div className={styles.supportHeroPanel} aria-label="Current Creata AI support highlights">
          <div className={styles.supportPanelHeader}>
            <AutoAwesomeIcon fontSize="small" />
            <span>Current product status</span>
          </div>
          <div className={styles.supportStatusList}>
            <p><strong>Available now</strong><span>iPhone app, daily free credits, chat-first home, Listen, summaries and questions about a page, Share to Creata and the Safari button, Library and reminders, images, Magic Edit, music, text to speech, voice clone, Voice Changer, and MP3, podcast and song (Starter and Pro).</span></p>
            <p><strong>In development</strong><span>Sound effects, and doing more on the device over time.</span></p>
            <p><strong>Roadmap</strong><span>Video creation will be marked as coming soon until it is ready.</span></p>
          </div>
        </div>
      </section>

      <section className={styles.supportQuickLinks} aria-label="Support topics">
        {quickLinks.map(([title, href, copy]) => (
          <a href={href} key={title} onClick={() => trackSupportClick(title)}>
            <span>{title}</span>
            <p>{copy}</p>
          </a>
        ))}
      </section>

      <section className={styles.supportStatusSection} aria-labelledby="status-title">
        <div className={styles.sectionIntro}>
          <p className={styles.eyebrow}>What is current</p>
          <h2 id="status-title">Product status at a glance.</h2>
          <p>This replaces the older subscription-only and device-download notes with the current iPhone and credits flow.</p>
        </div>
        <div className={styles.supportStatusGrid}>
          {productStatus.map(([name, status, copy]) => (
            <article className={styles.supportStatusCard} key={name}>
              <span>{status}</span>
              <h3>{name}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.supportFaqSection} aria-labelledby="faq-title">
        <div className={styles.sectionIntro}>
          <p className={styles.eyebrow}>Q&A</p>
          <h2 id="faq-title">Common support questions.</h2>
        </div>
        <div className={styles.supportFaqList}>
          {faqs.map((faq) => (
            <article className={styles.supportFaqItem} id={faq.id} key={faq.id}>
              <h3>{faq.title}</h3>
              <ul>
                {faq.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              {faq.action ? (
                <a
                  href={faq.action[1]}
                  onClick={() => trackSupportClick(faq.action[0])}
                >
                  {faq.action[0]} <ArrowForwardIcon fontSize="inherit" />
                </a>
              ) : null}
            </article>
          ))}
        </div>
      </section>

      <section className={styles.supportContactBand} aria-labelledby="contact-title">
        <MailOutlineIcon />
        <div>
          <p className={styles.eyebrow}>Need a person?</p>
          <h2 id="contact-title">Send the details once, and make them useful.</h2>
          <p>
            Include your Creata AI username, device model, app version if you can find it,
            and the shortest steps that reproduce the issue.
          </p>
        </div>
        <a
          className={styles.primaryCta}
          href="mailto:creataAi.com@gmail.com"
          onClick={() => trackSupportClick("email_footer")}
        >
          Email support <ArrowForwardIcon fontSize="small" />
        </a>
      </section>

      <MarketingFooter />
    </main>
  );
};

export default Support;
